import { nodesFlux } from '@candulabs/craft';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import Node from 'craft/components/Node';
import { MediaSourceButton } from 'craft/components/SelectedToolbar/MediaSourceButton';
import { stringWithFallbacks } from 'craft/containers/Actions';
import { useEditorNode, useEditorSelector, useNodeContext, useSelection } from 'craft/hooks';
import { Resolver, ToolboxComponentSections } from 'craft/types';

import AspectRatio from './AspectRatio';
import PlayerPlaceholder from './PlayerPlaceholder';
import { PlayerToolbox } from './PlayerToolbox';

export const VIDEOPLAYER_BASE_URL = `https://media.candulabs.com/videoPlayer.html`;
const PlayerWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const VideoOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0;
`;

const setPointerEvents = (el: HTMLElement, value: 'auto' | 'none') => {
  // eslint-disable-next-line no-param-reassign
  el.style.pointerEvents = value;
};

export const Player: Resolver = () => {
  const playerRef = useRef<HTMLIFrameElement>(null);

  const { id } = useNodeContext();
  const { setProps } = useEditorNode(id);
  const [playbackFailed, setPlaybackFailed] = useState(false);

  const { selection } = useSelection();
  const hasFocus = selection.all.length === 1 && selection.all[0] === id;

  const propsSelector = useMemo(() => nodesFlux.selectors.getNodeProps(id), [id]);
  const { src, height, width, aspectRatio } = useEditorSelector(propsSelector) ?? {};

  const onMouseEnter = () => {
    const el = playerRef.current;
    if (!el) {
      return;
    }
    setPointerEvents(el, 'auto');
  };

  const onMouseLeave = () => {
    const el = playerRef.current;
    if (!el) {
      return;
    }
    setPointerEvents(el, 'none');
  };

  const setVideoSource = (source: string) => {
    setPlaybackFailed(false);
    setProps({ src: source, width: '640px', height: '360px' });
  };

  useEffect(() => {
    window.onmessage = (event) => {
      if (event.data === `react-player-error-${src}`) {
        setPlaybackFailed(true);
      }
    };

    if (src) {
      setPlaybackFailed(false);
    }
  }, [src]);

  useEffect(() => {
    if (aspectRatio && !!height && height !== 'auto') {
      // we set aspect ratio to null because
      // in this case, height is being changed by the generic Size component.
      setProps({ aspectRatio: null });
    }
  }, [aspectRatio, height]);

  return (
    <Node style={{ height, width }}>
      <PlayerWrapper onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <AspectRatio aspectRatio={aspectRatio}>
          {src && !playbackFailed ? (
            <React.Fragment>
              {!hasFocus && <VideoOverlay />}
              <iframe
                ref={playerRef}
                title={`video-${id}`}
                src={`${VIDEOPLAYER_BASE_URL}?src=${encodeURIComponent(
                  stringWithFallbacks(src),
                )}&errorReporting=true`}
                style={{ pointerEvents: 'none' }}
                width="100%"
                height="100%"
              />
            </React.Fragment>
          ) : (
            <PlayerPlaceholder onSetVideoSource={setVideoSource} failed={playbackFailed} />
          )}
        </AspectRatio>
      </PlayerWrapper>
    </Node>
  );
};

Player.craft = {
  name: 'Player',
};

Player.resize = {
  shouldDisableHandlers: true,
};

Player.toolbox = {
  component: [PlayerToolbox],
  schema: {
    [ToolboxComponentSections.Layout]: {
      blockedDisplays: ['flex'],
    },
    [ToolboxComponentSections.Size]: {
      hasMinMaxSize: false,
      hasOverflow: true,
    },
    [ToolboxComponentSections.Background]: {
      hasBackgroundImage: false,
    },
  },
};

Player.selectedToolbar = {
  customItems: [MediaSourceButton],
};
